import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { GlossaryTerms } from "@/components/calv/GlossaryTerms";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { StackGrid } from "@/components/calv/StackGrid";
import { Callout } from "@/components/calv/Callout";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

export const Route = createFileRoute("/en/services/conversion-optimization")({
  head: () => {
    const h = pageHead("conversionWebOptimization", "en");
    return { meta: h.meta, links: h.links };
  },
  component: CWOPage,
});

const steps = [
  { num: "01", title: "Analysis of user behavior, funnels, and drop-off points", body: "We analyze how users actually move on the website or online shop – not how it was theoretically planned. Entry pages, user paths, drop-off points, performance by device. The goal is to understand exactly where users drop off – and why." },
  { num: "02", title: "Economic evaluation of identified weaknesses", body: "Not every weakness is automatically relevant – and not every optimization is economically sensible. We evaluate by number of affected users, impact on conversions, and implementation effort. This creates clear prioritization by economic leverage – not gut feeling." },
  { num: "03", title: "Optimization of structure, content, and user guidance", body: "Based on prioritization, we optimize the areas with the greatest leverage: page and content structure, argumentation logic, CTAs, forms and checkouts, mobile usage, technical performance. We consistently think from the user decision perspective." },
  { num: "04", title: "Integration with Performance Marketing & SEO", body: "Conversion optimization doesn't work in isolation, but across channels. Better conversion lowers cost per click or lead. Higher ROAS enables scaling. Organic traffic becomes economically usable. Often the decisive lever for profitable marketing." },
];

const capabilities = [
  { name: "Funnel analysis", detail: "Entry pages, drop-off points" },
  { name: "Economic prioritization", detail: "By leverage, not gut feeling" },
  { name: "Page structure & content", detail: "Argumentation logic, sequence" },
  { name: "CTA optimization", detail: "Placement, wording, context" },
  { name: "Forms & checkouts", detail: "Lead funnels, completion rates" },
  { name: "Mobile usage", detail: "Interaction, performance" },
  { name: "Technical performance", detail: "Load times, Core Web Vitals" },
  { name: "Cross-channel integration", detail: "With PPC, SEO, tracking" },
];

const stack = [
  { name: "Google Analytics (GA4)", small: "Behavior, funnel data" },
  { name: "Google Tag Manager", small: "Event tracking, maintenance" },
  { name: "Hotjar / Microsoft Clarity", small: "Heatmaps, session recordings" },
  { name: "PageSpeed Insights / Lighthouse", small: "Technical performance" },
];

function CWOPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          eyebrow="Conversion & Web Optimization"
          headline={<>Conversion and web optimization as <em>one craft.</em></>}
          lead="Hypothesis-driven, architecturally considered. From server-side tracking through landing-page architecture to design-system governance."
          breadcrumbs={[{ label: "Start", to: "/en" }, { label: "Services", to: "/en/services" }, { label: "Conversion & Web Optimization" }]}
        />

        <Section number="02" kicker="Standpoint" headline={<>Every percentage point of conversion <em>has direct impact.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Many companies invest significant budgets in performance marketing or SEO – yet only tap into a fraction of the potential. The reason is rarely lack of traffic. The reason is almost always a system that doesn't consistently guide users to the desired action.</p>
              <p className="mb-5">For us, conversion optimization doesn't mean cosmetic adjustments or isolated A/B tests. It means systematically analyzing where exactly users drop off, what information is missing – and how many conversions are currently being lost.</p>
              <p>Every percentage point more conversion directly affects revenue, CPL, CPA, and ROAS. For us, conversion optimization is not an isolated discipline, but an integral part of an economically considered online system — from first click to conversion.</p>
            </div>
          </RevealOnScroll>
          <Callout>Conversion optimization doesn't mean changing everything. But targeting specifically where measurable money is being lost.</Callout>
        </Section>

        <Section variant="tinted" number="03" kicker="Approach" headline={<>Four phases from <em>funnel</em> to leverage.</>}>
          <ProcessFlow steps={steps} />
          <Callout>Traffic without conversion is wasted potential. Conversion is the multiplier for all marketing channels.</Callout>
        </Section>

        <Section number="04" kicker="Capabilities & stack" headline={<>What we use to make conversion <em>steerable.</em></>}>
          <Capabilities items={capabilities} />
          <div className="mt-12">
            <p className="eyebrow mb-5">Stack</p>
            <StackGrid items={stack} />
          </div>
        </Section>

        <LogoReferences locale="en" number="05" />

        <RelatedServices
          locale="en"
          fromId="conversion-web-optimierung"
          number="06"
          items={relatedBySource["conversion-web-optimierung"]}
        />

        <Section number="07" kicker="Engagement">
          <CTABlock
            headline={<>How much potential is currently <em>untapped</em> on your website?</>}
            body="Want to understand where users drop off – and how to systematically change that?"
            primary={{ label: "Get in touch", to: "/en/contact" }}
            secondary={{ label: "All services", to: "/en/services" }}
          />
        </Section>
        <GlossaryTerms locale="en" ids={["conversion-rate-optimization", "conversion-rate", "ab-test", "funnel-analysis", "conversion-funnel"]} />
      </main>
      <Footer />
    </>
  );
}
